<template>
  <div class="max-w-4xl mx-auto p-6 rounded-2xl overflow-auto">
    <h2 class="text-2xl font-bold mb-4 text-center text-paletteDarkGreen">{{ $t('settings.title') }}</h2>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- User Information Section -->
      <div class="p-8 bg-white/95 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl">
        <div class="flex items-center space-x-4 mb-4">
          <img class="w-20 h-20 rounded-full" :src="generateIconURL(userStore.user.user_id)" alt="Default avatar">
          <h3 class="text-xl font-semibold">{{ userStore.user.user_name }}</h3>
        </div>
        <div class="flex items-center justify-between mb-4">
          <p class="text-lg font-medium">Current Plan: <span class="font-semibold">{{ userStore.user.pricing_plan }}</span></p>
            <router-link to="/dashboard" class="text-blue-600 hover:underline">Go to Dashboard</router-link>
        </div>

        <!-- Danger Zone -->
        <div class="mt-6 border-t pt-4">
          <h3 class="text-xl text-paletteOrange font-semibold">{{ $t('settings.dangerZone.title') }}</h3>
          <button @click="deleteAccount"
            class="w-full mt-2 bg-paletteOrange text-white py-2 rounded-lg hover:bg-red-700 transition">
            {{ $t('settings.dangerZone.deleteAccount') }}
          </button>
        </div>
      </div>

      <!-- Password Section -->
      <div class="p-8 bg-white/95 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl">
        <h3 class="text-xl font-semibold mb-4">{{ $t('settings.password.title') }}</h3>
        <form @submit.prevent="validateUpdatePassword" class="space-y-4">
          <div v-if="errorMessage" class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg text-sm">
            {{ errorMessage }}
          </div>
          <div v-if="successMessage" class="mb-4 p-3 text-green-700 bg-green-100 border border-green-400 rounded-lg text-sm">
            {{ successMessage }}
          </div>
          <div>
            <label for="currentPassword" :class="['block text-sm font-medium mb-1', validationState.current ? 'text-gray-700' : 'text-red-700']">
              {{ $t('settings.password.currentPassword') }}
            </label>
            <input id="currentPassword" v-model="passwords.current" type="password"
              :placeholder="$t('settings.password.currentPasswordPlaceholder')"
              @input="validateOnInput('current')"
              :class="['w-full p-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 sm:text-sm transition',
              validationState.current ? 'border-gray-300 focus:ring-paletteDarkGreen' : 'border-red-500 bg-red-50']">
            <p v-if="!validationState.current" class="mt-2 text-sm text-red-600">
              {{ $t('settings.password.currentPasswordError') }}
            </p>
          </div>
          <div>
            <label for="newPassword" :class="['block text-sm font-medium mb-1', validationState.new ? 'text-gray-700' : 'text-red-700']">
              {{ $t('settings.password.newPassword') }}
            </label>
            <input id="newPassword" v-model="passwords.new" type="password"
              :placeholder="$t('settings.password.newPasswordPlaceholder')"
              @input="validateNewPassword"
              :class="['w-full p-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 sm:text-sm transition',
              validationState.new ? 'border-gray-300 focus:ring-paletteDarkGreen' : 'border-red-500 bg-red-50']">
            <p v-if="!validationState.new" class="mt-2 text-sm text-red-600">
              {{ $t('settings.password.newPasswordError') }}
            </p>
          </div>
          <div>
            <label for="confirmPassword" :class="['block text-sm font-medium mb-1', validationState.confirm ? 'text-gray-700' : 'text-red-700']">
              {{ $t('settings.password.confirmNewPassword') }}
            </label>
            <input id="confirmPassword" v-model="passwords.confirm" type="password"
              :placeholder="$t('settings.password.confirmNewPasswordPlaceholder')"
              @input="validateOnInput('confirm')"
              :class="['w-full p-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 sm:text-sm transition',
              validationState.confirm ? 'border-gray-300 focus:ring-paletteDarkGreen' : 'border-red-500 bg-red-50']">
            <p v-if="!validationState.confirm" class="mt-2 text-sm text-red-600">
              {{ $t('settings.password.confirmNewPasswordError') }}
            </p>
          </div>
          <button type="submit" :disabled="!isFormValid" class="w-full bg-paletteDarkGreen text-white py-2 rounded-lg hover:bg-paletteLightGreen transition disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="isLoading" class="loader flex justify-center items-center">
              <LoaderIcon />
            </span>
            <template v-else>{{ $t('settings.password.saveChanges') }}</template>
          </button>
        </form>
        <!-- Password Requirements -->
        <div class="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 class="font-semibold">{{ $t('settings.password.requirements.title') }}</h4>
          <ul class="mt-2 text-sm space-y-1">
            <li :class="passwordValidations.length ? 'text-paletteLightGreen' : 'text-gray-400'">
              {{ $t('settings.password.requirements.length') }}
            </li>
            <li :class="passwordValidations.uppercase ? 'text-paletteLightGreen' : 'text-gray-400'">
              {{ $t('settings.password.requirements.uppercase') }}
            </li>
            <li :class="passwordValidations.lowercase ? 'text-paletteLightGreen' : 'text-gray-400'">
              {{ $t('settings.password.requirements.lowercase') }}
            </li>
            <li :class="passwordValidations.special ? 'text-paletteLightGreen' : 'text-gray-400'">
              {{ $t('settings.password.requirements.special') }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { updatePassword, EmailAuthProvider, getAuth, reauthenticateWithCredential } from 'firebase/auth'
import { computed, reactive, ref } from 'vue'
import { generateIconURL } from '@/utils/userUtils'
import { useUserStore } from '@/stores/users'
import { validatePassword, isPasswordValid } from '@/utils/passwordValidation'
import LoaderIcon from '../utils/LoaderIcon.vue'

const userStore = useUserStore()
const isLoading = ref(false)

const passwords = reactive({
  current: '',
  new: '',
  confirm: ''
})

const passwordValidations = reactive({
  length: false,
  uppercase: false,
  lowercase: false,
  special: false
})

const errorMessage = ref('')
const successMessage = ref('')
const validationState = reactive({
  current: true,
  new: true,
  confirm: true
})

const validateNewPassword = () => {
  Object.assign(passwordValidations, validatePassword(passwords.new))
  validationState.new = isPasswordValid(passwordValidations)
}

const validateOnInput = (field) => {
  if (field === 'current') {
    validationState.current = passwords.current.trim() !== ''
  } else if (field === 'confirm') {
    validationState.confirm = passwords.confirm === passwords.new
  }
  if (errorMessage.value) {
    errorMessage.value = ''
  }
  if (successMessage.value) {
    successMessage.value = ''
  }
}

const isFormValid = computed(() => {
  return validationState.current && validationState.new && validationState.confirm && Object.values(passwordValidations).every(Boolean)
})

const validateUpdatePassword = async () => {
  isLoading.value = true
  if (!isFormValid.value) {
    errorMessage.value = 'Please fix the errors above before submitting.'
    isLoading.value = false
    return
  }
  const user = getAuth().currentUser
  if (!user) {
    errorMessage.value = 'User not found. Please log in again.'
    isLoading.value = false
    return
  }

  const credential = EmailAuthProvider.credential(
    user.email,
    passwords.current
  )

  try {
    await reauthenticateWithCredential(user, credential)
    await updatePassword(user, passwords.new)
    successMessage.value = 'Your password has been successfully updated!'
    passwords.current = ''
    passwords.new = ''
    passwords.confirm = ''
  } catch (error) {
    console.log(error)
    errorMessage.value = error.message
  } finally {
    isLoading.value = false
  }
}

const deleteAccount = () => {
  if (confirm('Are you sure you want to delete your account? This action is irreversible. You will lose all remaining credits.')) {
    alert('Account deleted. JK! It\'s not implemented yet 🤪')
  }
}
</script>
